import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { activityLogApi, ActivityLogEntry, ActivityLogParams } from '../../api/activityLog';
import { usersApi } from '../../api/client';
import { Activity, Filter, RefreshCw } from 'lucide-react';

// Badge colors per action prefix
function getActionColor(action: string): { bg: string; text: string; border: string } {
  if (action === 'login') {
    return { bg: 'rgba(34,197,94,0.12)', text: '#22c55e', border: 'rgba(34,197,94,0.3)' };
  }
  if (action.startsWith('project.')) {
    return { bg: 'rgba(59,130,246,0.12)', text: '#3b82f6', border: 'rgba(59,130,246,0.3)' };
  }
  if (action.startsWith('invoice.')) {
    return { bg: 'rgba(234,179,8,0.12)', text: '#eab308', border: 'rgba(234,179,8,0.3)' };
  }
  if (action.startsWith('stock.')) {
    return { bg: 'rgba(249,115,22,0.12)', text: '#f97316', border: 'rgba(249,115,22,0.3)' };
  }
  return { bg: 'rgba(107,104,128,0.12)', text: '#6b6880', border: 'rgba(107,104,128,0.3)' };
}

function ActionBadge({ action }: { action: string }) {
  const colors = getActionColor(action);
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontFamily: 'JetBrains Mono, monospace',
      fontWeight: 600,
      background: colors.bg,
      color: colors.text,
      border: `1px solid ${colors.border}`,
      whiteSpace: 'nowrap',
    }}>
      {action}
    </span>
  );
}

function UserAvatar({ user }: { user: ActivityLogEntry['user'] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {user.avatar ? (
        <img
          src={user.avatar}
          alt=""
          style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid #33323d', flexShrink: 0 }}
        />
      ) : (
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: '#27262f', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 12, color: '#f97316', fontWeight: 700, flexShrink: 0,
        }}>
          {user.name[0]?.toUpperCase()}
        </div>
      )}
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#c4c2d4' }}>{user.name}</div>
        <div style={{ fontSize: 11, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>{user.email}</div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

const ACTION_OPTIONS = [
  { value: '', label: 'Todas las acciones' },
  { value: 'login', label: 'login' },
  { value: 'project.', label: 'project.*' },
  { value: 'invoice.', label: 'invoice.*' },
  { value: 'stock.', label: 'stock.*' },
];

export default function ActivityLogPage() {
  const [params, setParams] = useState<ActivityLogParams>({ limit: 50 });
  const [pendingParams, setPendingParams] = useState<ActivityLogParams>({ limit: 50 });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['activityLog', params],
    queryFn: () => activityLogApi.getActivityLog(params),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const users: Array<{ id: number; name: string }> = usersData?.data ?? [];

  const handleApplyFilters = () => {
    setParams({ ...pendingParams });
  };

  const handleReset = () => {
    const reset: ActivityLogParams = { limit: 50 };
    setPendingParams(reset);
    setParams(reset);
  };

  const logs: ActivityLogEntry[] = data?.data ?? [];

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'rgba(249,115,22,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Activity size={18} color="#f97316" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#f4f3f8', fontFamily: 'Syne, sans-serif' }}>
              Registro de Actividad
            </h1>
            <p style={{ margin: 0, fontSize: 12, color: '#4a4858' }}>
              Auditoría de acciones de usuarios
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 6,
            background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)',
            color: '#f97316', cursor: 'pointer', fontSize: 13,
            opacity: isFetching ? 0.6 : 1,
          }}
        >
          <RefreshCw size={14} style={{ animation: isFetching ? 'spin 1s linear infinite' : 'none' }} />
          Actualizar
        </button>
      </div>

      {/* Filters */}
      <div style={{
        background: '#16151c', border: '1px solid #27262f', borderRadius: 10,
        padding: '16px 20px', marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
          <Filter size={14} color="#6b6880" />
          <span style={{ fontSize: 12, color: '#6b6880', fontWeight: 500 }}>FILTROS</span>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Usuario */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>USUARIO</label>
            <select
              value={pendingParams.userId ?? ''}
              onChange={(e) => setPendingParams(p => ({ ...p, userId: e.target.value || undefined }))}
              style={{
                background: '#1c1b22', border: '1px solid #33323d', borderRadius: 6,
                color: '#c4c2d4', fontSize: 13, padding: '7px 10px', minWidth: 180,
              }}
            >
              <option value="">Todos los usuarios</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>

          {/* Acción */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>ACCIÓN</label>
            <select
              value={pendingParams.action ?? ''}
              onChange={(e) => setPendingParams(p => ({ ...p, action: e.target.value || undefined }))}
              style={{
                background: '#1c1b22', border: '1px solid #33323d', borderRadius: 6,
                color: '#c4c2d4', fontSize: 13, padding: '7px 10px', minWidth: 160,
              }}
            >
              {ACTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Desde */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>DESDE</label>
            <input
              type="date"
              value={pendingParams.from ?? ''}
              onChange={(e) => setPendingParams(p => ({ ...p, from: e.target.value || undefined }))}
              style={{
                background: '#1c1b22', border: '1px solid #33323d', borderRadius: 6,
                color: '#c4c2d4', fontSize: 13, padding: '7px 10px',
              }}
            />
          </div>

          {/* Hasta */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>HASTA</label>
            <input
              type="date"
              value={pendingParams.to ?? ''}
              onChange={(e) => setPendingParams(p => ({ ...p, to: e.target.value || undefined }))}
              style={{
                background: '#1c1b22', border: '1px solid #33323d', borderRadius: 6,
                color: '#c4c2d4', fontSize: 13, padding: '7px 10px',
              }}
            />
          </div>

          {/* Límite */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>LÍMITE</label>
            <select
              value={pendingParams.limit ?? 50}
              onChange={(e) => setPendingParams(p => ({ ...p, limit: parseInt(e.target.value) }))}
              style={{
                background: '#1c1b22', border: '1px solid #33323d', borderRadius: 6,
                color: '#c4c2d4', fontSize: 13, padding: '7px 10px',
              }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>

          {/* Botones */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleApplyFilters}
              style={{
                padding: '8px 16px', borderRadius: 6,
                background: '#f97316', border: 'none',
                color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              Aplicar
            </button>
            <button
              onClick={handleReset}
              style={{
                padding: '8px 16px', borderRadius: 6,
                background: 'transparent', border: '1px solid #33323d',
                color: '#6b6880', cursor: 'pointer', fontSize: 13,
              }}
            >
              Limpiar
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      {data && (
        <div style={{ marginBottom: 16, fontSize: 13, color: '#4a4858' }}>
          {data.total} registro{data.total !== 1 ? 's' : ''} encontrado{data.total !== 1 ? 's' : ''}
        </div>
      )}

      {/* Table */}
      <div style={{
        background: '#16151c', border: '1px solid #27262f', borderRadius: 10,
        overflow: 'hidden',
      }}>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#4a4858' }}>
            Cargando registros...
          </div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#4a4858' }}>
            <Activity size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <div>No hay registros de actividad</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #27262f' }}>
                  {['Fecha/Hora', 'Usuario', 'Acción', 'Descripción', 'IP'].map((h) => (
                    <th key={h} style={{
                      padding: '12px 16px', textAlign: 'left',
                      fontSize: 11, color: '#4a4858',
                      fontFamily: 'JetBrains Mono, monospace',
                      fontWeight: 600, letterSpacing: '0.05em',
                      background: '#111014',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr
                    key={log.id}
                    style={{
                      borderBottom: i < logs.length - 1 ? '1px solid #1e1d25' : 'none',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#1a1921')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 12, color: '#6b6880', fontFamily: 'JetBrains Mono, monospace' }}>
                        {formatDate(log.createdAt)}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <UserAvatar user={log.user} />
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <ActionBadge action={log.action} />
                    </td>
                    <td style={{ padding: '12px 16px', maxWidth: 360 }}>
                      <span style={{ fontSize: 13, color: '#c4c2d4' }}>{log.description}</span>
                      {log.entityType && (
                        <span style={{ fontSize: 11, color: '#4a4858', marginLeft: 8, fontFamily: 'JetBrains Mono, monospace' }}>
                          [{log.entityType}#{log.entityId}]
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 11, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>
                        {log.ipAddress ?? '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
